import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BUSINESS, GALLERY, IMG } from "@/lib/content";
import { goToEstimate } from "@/lib/scroll";
import { Eyebrow, Reveal } from "./Reveal";

const Tile = ({ g, i, onOpen, className }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 1, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    <button onClick={() => onOpen(g)} data-testid={`gallery-item-${i}`} className="group relative block h-full w-full overflow-hidden bg-[#151b26] text-left">
      <img src={g.src} alt={`${g.title} by KS Granite House`} loading="lazy" className="h-full w-full object-cover transition-[transform,filter] duration-[1.4s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-105 group-hover:brightness-110" />
      <span className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/90 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
      <span className="absolute inset-x-5 bottom-5 flex items-end justify-between gap-4">
        <span>
          <span className="block text-[11px] uppercase tracking-[0.2em] text-[#CDA85B]">{g.type}</span>
          <span className="mt-1 block font-display text-2xl text-[#F5F0E6]">{g.title}</span>
        </span>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/30 text-white transition-transform duration-500 group-hover:rotate-90"><Plus className="h-4 w-4" /></span>
      </span>
    </button>
  </motion.div>
);

const LAYOUT = [
  "sm:col-span-2 lg:col-span-5 lg:row-span-2 aspect-[3/4] lg:aspect-auto",
  "lg:col-span-4 lg:row-span-2 aspect-[3/4] lg:aspect-auto",
  "lg:col-span-3 aspect-[3/4] lg:aspect-auto",
  "lg:col-span-3 aspect-[3/4] lg:aspect-auto",
  "lg:col-span-4 aspect-[4/3] lg:aspect-auto",
];

const CtaTile = () => (
  <Reveal className="relative overflow-hidden sm:col-span-2 lg:col-span-8">
    <div className="relative flex h-full min-h-[300px] flex-col justify-end p-8 sm:p-10">
      <img src={IMG.whiteMarble} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F17] via-[#0B0F17]/85 to-[#0B0F17]/30" />
      <div className="relative">
        <p className="font-display text-4xl leading-tight text-[#F5F0E6] sm:text-5xl">Your kitchen <span className="italic text-[#CDA85B]">could be next.</span></p>
        <button onClick={() => goToEstimate()} data-testid="gallery-cta-btn" className="ks-btn-gold mt-6">Start My Free Estimate</button>
      </div>
    </div>
  </Reveal>
);

export const Gallery = () => {
  const [active, setActive] = useState(null);
  return (
    <section id="work" data-testid="gallery-section" className="bg-[#0B0F17] py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Reveal><Eyebrow>Our Work</Eyebrow></Reveal>
            <Reveal delay={0.1}><h2 className="mt-5 font-display text-4xl leading-[1.05] text-[#F5F0E6] sm:text-5xl lg:text-6xl">Real projects. <span className="italic text-[#CDA85B]">Real homes.</span></h2></Reveal>
          </div>
          <Reveal delay={0.2}>
            <a href={BUSINESS.instagram} target="_blank" rel="noreferrer" data-testid="gallery-instagram-link" className="ks-btn-ghost">
              <Instagram className="h-4 w-4" /> More on Instagram
            </a>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:auto-rows-[340px] lg:grid-cols-12">
          {GALLERY.map((g, i) => <Tile key={g.src} g={g} i={i} onOpen={setActive} className={LAYOUT[i]} />)}
          <CtaTile />
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="gallery-lightbox" className="max-h-[92vh] max-w-4xl border-white/10 bg-[#0B0F17] p-3 text-[#F5F0E6]">
          {active && (
            <figure>
              <img src={active.src} alt={active.title} className="max-h-[78vh] w-full object-contain" />
              <figcaption className="flex items-center justify-between px-2 pt-3">
                <DialogTitle className="font-display text-2xl font-normal">{active.title}</DialogTitle>
                <span className="text-xs uppercase tracking-[0.2em] text-[#CDA85B]">{active.type}</span>
              </figcaption>
            </figure>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
